<script setup lang="ts">
// The money moment. Every factor and its points in plain sight — no black
// boxes, no scare tactics. A REPAIR verdict is good news and reads that way;
// the CTA adapts to the verdict instead of always pushing quotes.
import { computed, onMounted, ref } from 'vue'
import type { WizardVerdict } from '../scorer'
import { scrollTo } from '../store'

const props = defineProps<{ verdict: WizardVerdict }>()

const emit = defineEmits<{ getQuotes: []; startOver: [] }>()

// Snappy count-up on reveal (skipped for reduced motion).
const shown = ref(0)
onMounted(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        shown.value = props.verdict.score
        return
    }
    const start = performance.now()
    const duration = 700
    const tick = (now: number) => {
        const t = Math.min((now - start) / duration, 1)
        shown.value = Math.round(props.verdict.score * (1 - Math.pow(1 - t, 3)))
        if (t < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
    // rAF pauses in background tabs — make sure the number always lands.
    setTimeout(() => {
        shown.value = props.verdict.score
    }, duration + 100)
})

const bandClass = computed(() => props.verdict.band.toLowerCase())

const headline = computed(
    () =>
        ({
            REPAIR: 'Repair it.',
            EITHER: 'It could go either way.',
            REPLACE: 'Replace it.',
        })[props.verdict.band],
)

const explainer = computed(
    () =>
        ({
            REPAIR: 'The math favors fixing what you have — that\'s good news. Keep it healthy and revisit if repairs start stacking up.',
            EITHER: 'Genuinely close. Get a repair quote AND a replacement quote, then run the numbers side by side — an on-site evaluation will tip it.',
            REPLACE: 'Putting more money into this system is likely throwing good money after bad. Get 2–3 replacement quotes and compare.',
        })[props.verdict.band],
)
</script>

<template>
    <div class="verdict" :class="bandClass">
        <div class="head">
            <span class="kicker">Our call</span>
            <span class="call">{{ headline }}</span>
            <span class="score"
                ><strong>{{ shown }}</strong
                >/100</span
            >
        </div>

        <ul class="factors">
            <li v-for="factor in verdict.factors" :key="factor.label">
                <span class="label">
                    {{ factor.label }}
                    <span v-if="factor.note" class="muted note">{{ factor.note }}</span>
                </span>
                <span class="points">{{ factor.points > 0 ? '+' : '' }}{{ factor.points }}</span>
            </li>
            <li v-if="verdict.factors.length === 0" class="muted">
                Nothing here leans toward replacement — that's a healthy system.
            </li>
        </ul>

        <p class="explainer muted">{{ explainer }}</p>
        <p class="shown muted">Every factor shown. Nothing hidden.</p>

        <div class="actions">
            <button
                v-if="verdict.band !== 'REPAIR'"
                class="cta"
                type="button"
                @click="emit('getQuotes')"
            >
                Get {{ verdict.band === 'REPLACE' ? '2–3 replacement quotes' : 'a pro evaluation' }}
            </button>
            <button v-else class="cta" type="button" @click="scrollTo('contact')">
                Keep it healthy — free reminders
            </button>
            <button class="cta cta--secondary" type="button" @click="emit('startOver')">
                Run it again
            </button>
        </div>
    </div>
</template>

<style scoped lang="scss">
.verdict {
    border: 2px solid var(--site-text);
    border-radius: var(--site-radius-md);
    background: var(--site-surface);
    box-shadow: var(--site-shadow-lg);
    overflow: hidden;

    .head {
        display: flex;
        align-items: baseline;
        gap: var(--site-space-md);
        padding: var(--site-space-md) var(--site-space-lg);
        border-bottom: 2px solid var(--site-text);

        .kicker {
            font-size: 0.75rem;
            font-weight: 700;
            letter-spacing: 0.14em;
            text-transform: uppercase;
            color: var(--site-text-muted);
        }

        .call {
            font-family: var(--site-font-heading);
            font-size: clamp(1.5rem, 4vw, 2.2rem);
            line-height: 1.1;
        }

        .score {
            margin-left: auto;
            font-variant-numeric: tabular-nums;
            color: var(--site-text-muted);

            strong {
                font-size: 1.6rem;
                color: var(--site-text);
            }
        }
    }

    &.replace .call {
        color: var(--site-danger);
    }

    &.repair .call {
        color: var(--site-success);
    }

    &.either .call {
        color: var(--site-accent);
    }

    .factors {
        list-style: none;
        margin: 0;
        padding: var(--site-space-sm) var(--site-space-lg);

        li {
            display: flex;
            justify-content: space-between;
            align-items: baseline;
            gap: var(--site-space-md);
            padding: 0.55rem 0;

            & + li {
                border-top: 1px solid var(--site-border);
            }

            .note {
                display: block;
                font-size: 0.82rem;
            }

            .points {
                font-variant-numeric: tabular-nums;
                font-weight: 700;
                white-space: nowrap;
            }
        }
    }

    .explainer {
        padding: 0 var(--site-space-lg);
        margin: 0 0 var(--site-space-sm);
    }

    .shown {
        padding: 0 var(--site-space-lg);
        font-size: 0.8rem;
        margin: 0 0 var(--site-space-md);
    }

    .actions {
        display: flex;
        gap: var(--site-space-sm);
        flex-wrap: wrap;
        padding: var(--site-space-md) var(--site-space-lg);
        border-top: 2px solid var(--site-text);
        background: var(--site-bg-alt);
    }
}
</style>
